import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import { parseNumber } from "../../utils/parseNumber";

export const useActions = ({
    workspace,
    confirmDisable,
    updateMaxCpc,
    enforceMaxCPCTargetImpressionShare,
    enforcePortfolioStrategy,
    disableKoBiddingManualMaxCpc,
    disableKoBiddingAutoMaxCpc,
    setLocalPatches,
    setModifiedRows,
    setConfirmDisable,
    updateKOBiddingAutoMaxCpc,
    data,
    refetch
}: any) => {

    const handleConfirmDisable = useCallback(
        async (row: any) => {

            if (!row) return;

            const modeToDisable =
                confirmDisable.mode;

            try {

                if (
                    modeToDisable ===
                    "KO Bidding Manual"
                ) {

                    const detail =
                        row.activations_details?.find(
                            (d: any) =>
                                d.mode === "KO Bidding Manual" &&
                                d.status === true
                        );

                    if (detail?.id) {

                        await disableKoBiddingManualMaxCpc({
                            id: detail.id
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            {
                                variant: "success"
                            }
                        );

                        setLocalPatches((prev: any) => {

                            const existing =
                                prev[row.id] ?? row;

                            return {
                                ...prev,

                                [row.id]: {
                                    ...existing,

                                    activations_details:
                                        existing.activations_details.filter(
                                            (d: any) =>
                                                d.mode !==
                                                "KO Bidding Manual"
                                        ),

                                    max_cpc: null,
                                    max_cpc_portfolio_strategy: null,
                                    target_cpa: null,
                                    target_cpa_portfolio_strategy: null,
                                    target_roas: null,
                                    target_roas_portfolio_strategy: null,
                                    target_impression_share: null,
                                    target_impression_share_position: null,
                                }
                            };
                        });
                    }
                }

                if (
                    modeToDisable?.startsWith(
                        "KO Bidding Auto"
                    )
                ) {

                    const detail =
                        row.activations_details?.find(
                            (d: any) =>
                                d.mode?.startsWith(
                                    "KO Bidding Auto"
                                ) &&
                                d.status === true
                        );

                    if (detail?.id) {

                        await disableKoBiddingAutoMaxCpc({
                            id: detail.id
                        }).unwrap();

                        enqueueSnackbar(
                            `Activation disabled successfully`,
                            {
                                variant: "success"
                            }
                        );

                        setLocalPatches((prev: any) => {

                            const existing =
                                prev[row.id] ?? row;

                            return {
                                ...prev,

                                [row.id]: {
                                    ...existing,

                                    activations_details:
                                        existing.activations_details.filter(
                                            (d: any) =>
                                                !d.mode?.startsWith(
                                                    "KO Bidding Auto"
                                                )
                                        ),
                                }
                            };
                        });
                    }
                }

                setConfirmDisable({
                    open: false,
                    row: null,
                    mode: null
                });

            } catch (err) {

                enqueueSnackbar(
                    `Failed to disable ${modeToDisable}`,
                    {
                        variant: "error"
                    }
                );
            }
        },
        [
            confirmDisable,
            setConfirmDisable,
            disableKoBiddingManualMaxCpc,
            disableKoBiddingAutoMaxCpc,
            setLocalPatches
        ]
    );

    const handleUpdateMaxCPC = useCallback(
        async (modifiedRows: any[]) => {
            /**
             * Conserve :
             * - undefined => champ non modifié
             * - null      => champ à vider en base
             * - valeur    => nombre
             *
             * Évite surtout Number(null) === 0.
             */
            const normalizeNumber = (
                value: any
            ): number | null | undefined => {
                if (value === undefined) {
                    return undefined;
                }

                if (value === null || value === "") {
                    return null;
                }

                const parsedValue = Number(value);

                return Number.isNaN(parsedValue)
                    ? null
                    : parsedValue;
            };

            try {
                // =====================================================
                // KO BIDDING MANUAL
                // =====================================================
                const manualRows = modifiedRows.filter((row) => {
                    const original = data.find(
                        (item: any) => item.id === row.rowId
                    );

                    return original?.activations_details?.some(
                        (activation: any) =>
                            activation.mode === "KO Bidding Manual" &&
                            activation.status === true
                    );
                });

                // =====================================================
                // KO BIDDING AUTO
                // =====================================================
                const autoRows = modifiedRows.filter((row) => {
                    const original = data.find(
                        (item: any) => item.id === row.rowId
                    );

                    return original?.activations_details?.some(
                        (activation: any) =>
                            activation.mode?.startsWith(
                                "KO Bidding Auto"
                            ) &&
                            activation.status === true
                    );
                });

                // =====================================================
                // UPDATE MANUAL
                // =====================================================
                if (manualRows.length > 0) {
                    const payload = manualRows.map((row) => ({
                        id: row.id,
                        workspaceId: workspace?.id,

                        // MAX CPC
                        max_cpc:
                            normalizeNumber(row.max_cpc),

                        max_cpc_portfolio_strategy:
                            normalizeNumber(
                                row.max_cpc_portfolio_strategy
                            ),

                        // TARGET CPA
                        target_cpa:
                            normalizeNumber(row.target_cpa),

                        target_cpa_portfolio_strategy:
                            normalizeNumber(
                                row.target_cpa_portfolio_strategy
                            ),

                        // TARGET ROAS
                        target_roas:
                            normalizeNumber(row.target_roas),

                        target_roas_portfolio_strategy:
                            normalizeNumber(
                                row.target_roas_portfolio_strategy
                            ),

                        // TARGET IMPRESSION SHARE
                        target_impression_share:
                            normalizeNumber(
                                row.target_impression_share
                            ),

                        target_impression_share_position:
                            row.target_impression_share_position,
                    }));

                    await updateMaxCpc(payload).unwrap();
                }

                // =====================================================
                // UPDATE AUTO
                // =====================================================
                if (autoRows.length > 0) {
                    const autoPayload = autoRows.map((row) => ({
                        id: row.id,
                        workspaceId: workspace?.id,

                        target_roas:
                            normalizeNumber(row.target_roas),

                        target_roas_portfolio_strategy:
                            normalizeNumber(
                                row.target_roas_portfolio_strategy
                            ),
                    }));

                    await updateKOBiddingAutoMaxCpc(
                        autoPayload
                    ).unwrap();
                }

                await refetch();

                setModifiedRows([]);

                enqueueSnackbar(
                    "Updates applied successfully",
                    {
                        variant: "success",
                    }
                );
            } catch (error) {
                console.error(
                    "Failed to update bidding values",
                    error
                );

                setLocalPatches({});
                setModifiedRows([]);

                enqueueSnackbar(
                    "Failed to apply updates",
                    {
                        variant: "error",
                    }
                );
            }
        },
        [
            data,
            refetch,
            workspace?.id,
            updateMaxCpc,
            updateKOBiddingAutoMaxCpc,
            setLocalPatches,
            setModifiedRows,
        ]
    );

    const handleFinish = useCallback(
        async (
            rows: any[],
            workspaceId: number,
            setResponse: (res: any[]) => void
        ) => {

            const buildPayload = (rowsToMap: any[]) => ({
                campaigns: rowsToMap.map((row: any) => {

                    const activeActivation =
                        row.activations_details?.find(
                            (detail: any) => detail.status === true
                        );

                    return {
                        account_id: String(row.account_id),
                        client: String(row.client_id),

                        campaign_id: String(
                            activeActivation?.campaign_id?.[0]
                            ?? row.campaign_id
                        ),

                        max_bid: row.max_cpc_to_enforce,

                        max_cpc_portfolio_strategy:
                            row.max_cpc_to_enforce,

                        target_percentage:
                            activeActivation?.target_impression_share,

                        position:
                            activeActivation?.target_impression_share_position,

                        bidding_strategy_type:
                            row.bidding_strategy_type,

                        platform:
                            activeActivation?.platform
                            ?? (
                                row?.platform === "GOOGLE"
                                    ? "Google Ads"
                                    : row?.platform === "BING"
                                        ? "Bing Ads"
                                        : row?.platform
                            )
                    };
                })
            });

            try {

                const targetImpressionShareRows = rows.filter(
                    (row: any) =>
                        row.bidding_strategy_type ===
                        "Target Impression Share"
                );

                const portfolioRows = rows.filter(
                    (row: any) =>
                        row.bidding_strategy_type !==
                        "Target Impression Share"
                );

                let responses: any[] = [];

                if (targetImpressionShareRows.length > 0) {
                    const response =
                        await enforceMaxCPCTargetImpressionShare(
                            buildPayload(targetImpressionShareRows)
                        ).unwrap();

                    responses = [...responses, ...response];
                }

                if (portfolioRows.length > 0) {
                    const response =
                        await enforcePortfolioStrategy(
                            buildPayload(portfolioRows)
                        ).unwrap();

                    responses = [...responses, ...response];
                }

                setResponse(responses);

            } catch (error) {
                console.error("Enforce error", error);
            }
        },
        [
            enforceMaxCPCTargetImpressionShare,
            enforcePortfolioStrategy
        ]
    );

    const handleRowUpdate = useCallback(
        (newRow: any, oldRow: any) => {
            // =====================================================
            // ACTIVATIONS
            // =====================================================
            const koManual = newRow.activations_details?.find(
                (d: any) =>
                    d.mode === "KO Bidding Manual" &&
                    d.status === true
            );

            const koAuto = newRow.activations_details?.find(
                (d: any) =>
                    d.mode?.startsWith("KO Bidding Auto") &&
                    d.status === true
            );

            const activeActivation = koManual || koAuto;

            const isManualMode = Boolean(koManual);
            const isAutoMode = Boolean(koAuto);

            // =====================================================
            // PLATFORM / STRATEGY RULES
            // =====================================================
            const platform = String(newRow.platform || "").toUpperCase();
            const strategy = String(
                newRow.bidding_strategy_type || ""
            ).trim();

            const isGoogleAds =
                platform === "GOOGLE" ||
                platform === "GOOGLE ADS";

            const usesPortfolioMaxCpc =
                isGoogleAds &&
                [
                    "Target ROAS",
                    "Maximize Conversion Value",
                    "Target CPA",
                    "Maximize Conversions",
                ].includes(strategy);

            const usesPortfolioRoas =
                isGoogleAds &&
                [
                    "Target ROAS",
                    "Maximize Conversion Value",
                ].includes(strategy);

            const usesPortfolioCpa =
                isGoogleAds &&
                [
                    "Target CPA",
                    "Maximize Conversions",
                ].includes(strategy);

            // =====================================================
            // HELPERS
            // =====================================================
            const isInvalidNumber = (value: number | null) =>
                value === null ||
                Number.isNaN(value) ||
                value < 0.01;

            // =====================================================
            // VALUES
            // =====================================================
            const nextMaxCpc = parseNumber(newRow.max_cpc);
            const oldMaxCpc = parseNumber(oldRow.max_cpc);

            const nextTargetCpa = parseNumber(newRow.target_cpa);
            const oldTargetCpa = parseNumber(oldRow.target_cpa);

            const nextTargetRoas = parseNumber(newRow.target_roas);
            const oldTargetRoas = parseNumber(oldRow.target_roas);

            const nextTargetIS = parseNumber(
                newRow.target_impression_share
            );

            const oldTargetIS = parseNumber(
                oldRow.target_impression_share
            );

            const nextTargetISPosition =
                newRow.target_impression_share_position;

            const oldTargetISPosition =
                oldRow.target_impression_share_position;

            // =====================================================
            // CHANGES
            // =====================================================
            const maxCpcChanged =
                nextMaxCpc !== oldMaxCpc;

            const targetCpaChanged =
                nextTargetCpa !== oldTargetCpa;

            const targetRoasChanged =
                nextTargetRoas !== oldTargetRoas;

            const targetISChanged =
                nextTargetIS !== oldTargetIS;

            const targetISPositionChanged =
                nextTargetISPosition !== oldTargetISPosition;

            if (
                !maxCpcChanged &&
                !targetCpaChanged &&
                !targetRoasChanged &&
                !targetISChanged &&
                !targetISPositionChanged
            ) {
                return oldRow;
            }

            // =====================================================
            // VALIDATION
            // =====================================================
            const isInvalidMaxCpc =
                Boolean(activeActivation) &&
                maxCpcChanged &&
                isInvalidNumber(nextMaxCpc);

            const isInvalidTargetCpa =
                Boolean(activeActivation) &&
                targetCpaChanged &&
                isInvalidNumber(nextTargetCpa);

            const isInvalidTargetRoas =
                Boolean(activeActivation) &&
                targetRoasChanged &&
                isInvalidNumber(nextTargetRoas);

            const isInvalidTargetIS =
                Boolean(activeActivation) &&
                targetISChanged &&
                isInvalidNumber(nextTargetIS);

            const isInvalidTargetISPosition =
                Boolean(activeActivation) &&
                targetISPositionChanged &&
                !nextTargetISPosition;

            const isInvalid =
                isInvalidMaxCpc ||
                isInvalidTargetCpa ||
                isInvalidTargetRoas ||
                isInvalidTargetIS ||
                isInvalidTargetISPosition;

            // =====================================================
            // MODIFIED ROWS / API PAYLOAD
            // =====================================================
            setModifiedRows((prev: any[]) => {
                const filtered = prev.filter(
                    (row: any) => row.rowId !== newRow.id
                );

                const existing = prev.find(
                    (row: any) => row.rowId === newRow.id
                );

                if (isInvalid) {
                    return filtered;
                }

                const patch: any = {
                    ...existing,
                    rowId: newRow.id,
                    id: isManualMode
                        ? koManual?.id
                        : koAuto?.id,
                };

                // =================================================
                // MAX CPC
                // =================================================
                if (maxCpcChanged) {
                    if (usesPortfolioMaxCpc) {
                        patch.max_cpc_portfolio_strategy =
                            nextMaxCpc;

                        patch.max_cpc = null;
                    } else {
                        patch.max_cpc =
                            nextMaxCpc;

                        patch.max_cpc_portfolio_strategy = null;
                    }
                }

                // =================================================
                // TARGET CPA
                // =================================================
                if (targetCpaChanged) {
                    if (usesPortfolioCpa) {
                        patch.target_cpa_portfolio_strategy =
                            nextTargetCpa;

                        patch.target_cpa = null;
                    } else {
                        patch.target_cpa =
                            nextTargetCpa;

                        patch.target_cpa_portfolio_strategy = null;
                    }
                }

                // =================================================
                // TARGET ROAS
                // =================================================
                if (targetRoasChanged) {
                    if (usesPortfolioRoas) {
                        patch.target_roas_portfolio_strategy =
                            nextTargetRoas;

                        patch.target_roas = null;
                    } else {
                        patch.target_roas =
                            nextTargetRoas;

                        patch.target_roas_portfolio_strategy = null;
                    }
                }

                // =================================================
                // TARGET IMPRESSION SHARE
                // =================================================
                if (targetISChanged) {
                    patch.target_impression_share =
                        nextTargetIS;
                }

                // =================================================
                // TARGET IMPRESSION SHARE POSITION
                // =================================================
                if (targetISPositionChanged) {
                    patch.target_impression_share_position =
                        nextTargetISPosition;
                }

                return [...filtered, patch];
            });

            // =====================================================
            // LOCAL UPDATED ROW
            // =====================================================
            const updatedRow = {
                ...newRow,

                activations_details:
                    newRow.activations_details?.map(
                        (activation: any) => {
                            const isCurrentActivation =
                                (
                                    isManualMode &&
                                    activation.id === koManual?.id
                                ) ||
                                (
                                    isAutoMode &&
                                    activation.id === koAuto?.id
                                );

                            if (!isCurrentActivation) {
                                return activation;
                            }

                            return {
                                ...activation,

                                // =================================
                                // MAX CPC
                                // =================================
                                ...(maxCpcChanged && {
                                    max_cpc:
                                        usesPortfolioMaxCpc
                                            ? null
                                            : nextMaxCpc,

                                    max_cpc_portfolio_strategy:
                                        usesPortfolioMaxCpc
                                            ? nextMaxCpc
                                            : null,
                                }),

                                // =================================
                                // TARGET CPA
                                // =================================
                                ...(targetCpaChanged && {
                                    target_cpa:
                                        usesPortfolioCpa
                                            ? null
                                            : nextTargetCpa,

                                    target_cpa_portfolio_strategy:
                                        usesPortfolioCpa
                                            ? nextTargetCpa
                                            : null,
                                }),

                                // =================================
                                // TARGET ROAS
                                // =================================
                                ...(targetRoasChanged && {
                                    target_roas:
                                        usesPortfolioRoas
                                            ? null
                                            : nextTargetRoas,

                                    target_roas_portfolio_strategy:
                                        usesPortfolioRoas
                                            ? nextTargetRoas
                                            : null,
                                }),

                                // =================================
                                // TARGET IS
                                // =================================
                                ...(targetISChanged && {
                                    target_impression_share:
                                        nextTargetIS,
                                }),

                                // =================================
                                // TARGET IS POSITION
                                // =================================
                                ...(targetISPositionChanged && {
                                    target_impression_share_position:
                                        nextTargetISPosition,
                                }),
                            };
                        }
                    ),

                isEdited: true,
                isInvalid,
            };

            return updatedRow;
        },
        [setModifiedRows]
    );

    return {
        handleConfirmDisable,
        handleFinish,
        handleUpdateMaxCPC,
        handleRowUpdate
    };
};